import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Typography,
    Paper,
    Divider,
    RadioGroup,
    Radio,
    Button,
    FormControlLabel,
    Alert,
    Link,
} from '@mui/material';

export interface Step1SimData {
    referenceYear: number;
    hasSim: boolean | null;
    simActive: boolean | null;
    simEstablishments: number | '';
    hasVet: boolean | null;
    joinedSusaf: boolean | null;
    wantsSusaf: boolean | null;
}

interface Step1Props {
    onNext: () => void;
    onChange: (data: Step1SimData) => void;
    data: Step1SimData;
    disabled?: boolean;
    onBack?: () => void;
}

const Step1: React.FC<Step1Props> = ({
    onNext,
    onChange,
    data,
    disabled = false,
    onBack,
}) => {
    const currentYear = new Date().getFullYear();

    const [hasSim, setHasSim] = useState<boolean | null>(data.hasSim ?? null);
    const [simActive, setSimActive] = useState<boolean | null>(data.simActive ?? null);
    const [simEstablishments, setSimEstablishments] = useState<number | ''>(data.simEstablishments ?? '');
    const [hasVet, setHasVet] = useState<boolean | null>(data.hasVet ?? null);
    const [joinedSusaf, setJoinedSusaf] = useState<boolean | null>(data.joinedSusaf ?? null);
    const [wantsSusaf, setWantsSusaf] = useState<boolean | null>(data.wantsSusaf ?? null);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        setHasSim(data.hasSim ?? null);
        setSimActive(data.simActive ?? null);
        setSimEstablishments(data.simEstablishments ?? '');
        setHasVet(data.hasVet ?? null);
        setJoinedSusaf(data.joinedSusaf ?? null);
        setWantsSusaf(data.wantsSusaf ?? null);
    }, [data]);

    useEffect(() => {
        onChange({
            referenceYear: currentYear,
            hasSim,
            simActive,
            simEstablishments,
            hasVet,
            joinedSusaf,
            wantsSusaf,
        });
        // eslint-disable-next-line
    }, [hasSim, simActive, simEstablishments, hasVet, joinedSusaf, wantsSusaf]);

    const handleSubmit = () => {
        const newErrors: { [key: string]: string } = {};

        if (hasSim === null) newErrors.hasSim = 'Campo obrigatório.';
        if (hasSim) {
            if (simActive === null) newErrors.simActive = 'Campo obrigatório.';
            if (simEstablishments === '' || isNaN(Number(simEstablishments)) || !Number.isInteger(Number(simEstablishments)) || Number(simEstablishments) < 0) {
                newErrors.simEstablishments = 'Informe um número inteiro válido.';
            }
            if (hasVet === null) newErrors.hasVet = 'Campo obrigatório.';
            if (joinedSusaf === null) newErrors.joinedSusaf = 'Campo obrigatório.';
            if (wantsSusaf === null) newErrors.wantsSusaf = 'Campo obrigatório.';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onNext();
        }
    };

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    SIM (Serviço de Inspeção Municipal)
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TextField
                    label="Ano de referência do Questionário"
                    type="number"
                    value={data.referenceYear}
                    onChange={e => onChange({
                        ...data,
                        referenceYear: Number(e.target.value)
                    })}
                    inputProps={{ min: 1900, max: 2100 }}
                    fullWidth
                    required
                    disabled={disabled}
                    sx={{ opacity: 0.5 }}
                    margin="normal"
                />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Preencha todos os campos obrigatórios. Campos condicionais serão exibidos conforme suas respostas.
                </Alert>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            O município possui Serviço de Inspeção Municipal (SIM) implantado? <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <RadioGroup
                            row
                            value={hasSim === null ? '' : hasSim ? 'sim' : 'nao'}
                            onChange={e => setHasSim(e.target.value === 'sim')}
                            name="has-sim"
                        >
                            <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                            <FormControlLabel value="nao" control={<Radio />} label="Não" />
                        </RadioGroup>
                        {errors.hasSim && <Typography color="error">{errors.hasSim}</Typography>}
                    </Grid>

                    {hasSim && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O Serviço de Inspeção Municipal (SIM) está ativo (em funcionamento)? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={simActive === null ? '' : simActive ? 'sim' : 'nao'}
                                    onChange={e => setSimActive(e.target.value === 'sim')}
                                    name="sim-active"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.simActive && <Typography color="error">{errors.simActive}</Typography>}
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Quantos empreendimentos/estabelecimentos possuem SIM no município?"
                                    type="number"
                                    //value={localEscolas.length > 0 ? localEscolas.length : ''}
                                    value={String(simEstablishments).length > 0 ? String(simEstablishments) : ''}
                                    onChange={e => setSimEstablishments(e.target.value === '' ? '' : Number(e.target.value))}
                                    error={!!errors.simEstablishments}
                                    helperText={errors.simEstablishments}
                                    margin="dense"
                                    required
                                    fullWidth
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município possui médico veterinário exclusivo disponível para atender o SIM? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={hasVet === null ? '' : hasVet ? 'sim' : 'nao'}
                                    onChange={e => setHasVet(e.target.value === 'sim')}
                                    name="has-vet"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.hasVet && <Typography color="error">{errors.hasVet}</Typography>}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município fez adesão ao Sistema Unificado Estadual de Sanidade Agroindustrial Familiar e de Pequeno Porte - SUSAF/MT? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={joinedSusaf === null ? '' : joinedSusaf ? 'sim' : 'nao'}
                                    onChange={e => setJoinedSusaf(e.target.value === 'sim')}
                                    name="joined-susaf"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.joinedSusaf && <Typography color="error">{errors.joinedSusaf}</Typography>}
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    <strong>Nota explicativa:</strong> Para informações sobre o SUSAF/MT, vide Lei estadual nº 10.502, de 18/01/2017, e Decreto nº 218, de 21/08/2019. Para mais informações{' '}
                                    <Link href="http://www.agriculturafamiliar.mt.gov.br/susaf2" target="_blank" rel="noopener">
                                        “SUSAF”
                                    </Link>.
                                </Alert>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município tem interesse em aderir ao SUSAF/MT? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={wantsSusaf === null ? '' : wantsSusaf ? 'sim' : 'nao'}
                                    onChange={e => setWantsSusaf(e.target.value === 'sim')}
                                    name="wants-susaf"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.wantsSusaf && <Typography color="error">{errors.wantsSusaf}</Typography>}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    disabled={disabled}
                >
                    Salvar e continuar
                </Button>
            </Box>
        </Box>
    );
};

export default Step1;